import { useEffect, useState } from 'react'
import { ListPlus, Plus, Trash2 } from 'lucide-react'
import { Button, WORKER_ROLE_LABELS, type WorkerRole } from '../../core/index.js'
import type { FacilityUnit, MinimumStaffingLine, SchedulingRules, ShiftTypeKey } from '../types.js'

/**
 * The staffing rules (scheduling rules plan phase D), on the Details tab under the facility's record: the
 * facility's units, the credentials each role must hold, and the minimum
 * staffing lines the facility-wide run and the coverage view judge each date
 * by. Part of the scheduling block, so saved with the settings tabs' draft.
 */

const ROLES: readonly WorkerRole[] = ['RN', 'CNA', 'LPN']
const INPUT_CLASS = 'rounded border border-marine/30 bg-white px-2 py-1.5 text-sm text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'
const SHIFT_TYPE_LABELS: Record<ShiftTypeKey, string> = { day: 'Day', evening: 'Evening', night: 'Night' }

/** A text field that commits on leaving it, so "BLS, " is not cut short while it is being typed. */
function CommitOnBlur({ value, onCommit, label, placeholder, className }: { value: string; onCommit: (text: string) => void; label: string; placeholder?: string; className?: string }) {
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  return <input aria-label={label} placeholder={placeholder} className={className ?? INPUT_CLASS} value={text} onChange={(event) => setText(event.target.value)} onBlur={() => onCommit(text)} />
}

function codesOf(text: string): string[] {
  return [...new Set(text.split(/[;,]/).map((code) => code.trim().toUpperCase()).filter((code) => code !== ''))]
}

interface FacilityStaffingRulesProps {
  rules: SchedulingRules
  onChange: (patch: Partial<SchedulingRules>) => void
  /** The units the facility's published shifts name, offered as the list while it keeps none. */
  shiftUnitIds?: readonly string[]
}

/** The units the published shifts name, as rows to list: the id as the name until someone renames it. */
export function unitsFromShifts(unitIds: readonly string[]): FacilityUnit[] {
  return [...new Set(unitIds.map((id) => id.trim()).filter((id) => id !== ''))].sort().map((id) => ({ id, name: id }))
}

export function FacilityStaffingRules({ rules, onChange, shiftUnitIds = [] }: FacilityStaffingRulesProps) {
  const fromShifts = rules.units.length === 0 ? unitsFromShifts(shiftUnitIds) : []
  const offeredTypes = rules.shiftTypes.filter((entry) => entry.enabled).map((entry) => entry.key)
  const setUnit = (index: number, patch: Partial<FacilityUnit>): void => onChange({ units: rules.units.map((unit, at) => (at === index ? { ...unit, ...patch } : unit)) })
  const setLine = (index: number, patch: Partial<MinimumStaffingLine>): void => onChange({ minimumStaffing: rules.minimumStaffing.map((line, at) => (at === index ? { ...line, ...patch } : line)) })
  const setRoleCodes = (role: WorkerRole, codes: string[]): void => {
    const { [role]: _dropped, ...others } = rules.credentials.requiredByRole
    onChange({ credentials: { requiredByRole: codes.length > 0 ? { ...others, [role]: codes } : others } })
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-xs font-bold text-ink">Units</div>
        <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">The units published shifts name. A worker with units on their record is only scheduled, offered swaps and open shifts on their home unit and the ones they float to; one with none recorded may work any unit. Keeping the list here names the units in the minimums below. Beds cap a forecast census; the planned census is what a day with no count and no history is planned on.</p>
        {rules.units.length > 0 && (
          <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">
            The <span className="font-bold text-ink">id</span> is the code published shifts, worker records and imports carry (the <span className="font-mono">unitId</span> column), so keep it once it is in use. The <span className="font-bold text-ink">name</span> is what people see, on the Publish shifts and Census tools and the staffing report, and can change at any time; left blank, it is the id.
          </p>
        )}
        {rules.units.length === 0 && (
          <div className="rounded border border-coral/40 bg-coral/5 p-3 text-[11px] text-ink leading-relaxed">
            <p>No units listed, so no census can be recorded and the staffing plan requires nothing: both are reckoned per unit. List each unit here, with its beds and planned census, then set the plan on the Scheduling tab's Census group.</p>
            {fromShifts.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-ink-mid">The published shifts name {fromShifts.map((unit) => unit.id).join(', ')}.</span>
                <Button icon={ListPlus} size="sm" onClick={() => onChange({ units: fromShifts })}>
                  {fromShifts.length === 1 ? 'List it as a unit' : `List these ${fromShifts.length} units`}
                </Button>
              </div>
            )}
          </div>
        )}
        {rules.units.length > 0 && (
          // Headings over the columns, the same widths as the fields below, so each box says what it is once it holds a value.
          <div aria-hidden className="flex items-end gap-2 pb-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            <span className="w-36">Id</span>
            <span className="flex-1">Name</span>
            <span className="w-20 text-right">Beds</span>
            <span className="w-20 text-right">Planned census</span>
            <span className="w-7" />
          </div>
        )}
        {rules.units.map((unit, index) => (
          <div key={index} className="flex items-center gap-2 py-1">
            <input aria-label="Unit id" className={`${INPUT_CLASS} w-36 font-mono`} value={unit.id} onChange={(event) => setUnit(index, { id: event.target.value })} placeholder="2-South" />
            <input aria-label="Unit name" className={`${INPUT_CLASS} flex-1`} value={unit.name} onChange={(event) => setUnit(index, { name: event.target.value })} placeholder="2 South – Med/Surg" />
            <input type="number" min={1} aria-label={`Beds on ${unit.name || unit.id}`} placeholder="optional" className={`${INPUT_CLASS} w-20 text-right`} value={unit.beds ?? ''} onChange={(event) => setUnit(index, { beds: event.target.value === '' ? null : Math.max(1, Math.round(Number(event.target.value) || 1)) })} />
            <input type="number" min={0} aria-label={`Planned census on ${unit.name || unit.id}`} placeholder="optional" className={`${INPUT_CLASS} w-20 text-right`} value={unit.plannedCensus ?? ''} onChange={(event) => setUnit(index, { plannedCensus: event.target.value === '' ? null : Math.max(0, Math.round(Number(event.target.value) || 0)) })} />
            <button type="button" aria-label={`Remove unit ${unit.id}`} className="p-1.5 text-ink-mid hover:text-coral" onClick={() => onChange({ units: rules.units.filter((_, at) => at !== index) })}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button icon={Plus} variant="secondary" size="sm" className="mt-2" onClick={() => onChange({ units: [...rules.units, { id: '', name: '' }] })}>
          Add a unit
        </Button>
      </div>

      <div>
        <div className="text-xs font-bold text-ink">Credentials each role must hold</div>
        <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">Codes separated by commas. A worker missing one, or holding one that lapses before the shift's day, is not scheduled, swapped onto or offered the shift. A shift can ask for more of its own when it is published.</p>
        {ROLES.map((role) => (
          <div key={role} className="flex items-center justify-between gap-4 py-1.5 border-b border-marine/10 last:border-b-0">
            <span className="text-xs text-ink-mid">
              {role} · {WORKER_ROLE_LABELS[role]}
            </span>
            <CommitOnBlur label={`Credentials every ${role} must hold`} placeholder="None required" className={`${INPUT_CLASS} w-56 font-mono`} value={(rules.credentials.requiredByRole[role] ?? []).join(', ')} onCommit={(text) => setRoleCodes(role, codesOf(text))} />
          </div>
        ))}
      </div>

      <div>
        <div className="text-xs font-bold text-ink">Minimum staffing</div>
        <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">At least this many of a role on a unit for every shift of a type, or - for a state ratio such as 1 CNA per 10 residents - that many for every so many residents of the day's census, rounded up. The month's run lists each date below a line, and where PTO is decided by the run, a PTO day that would leave a line short is denied when the worker could fill it. A ratio line is judged only on days with a census or a forecast.</p>
        {rules.minimumStaffing.length === 0 && <p className="text-[11px] text-ink-mid">No minimums. The run judges no coverage, and PTO left for the run is approved.</p>}
        {rules.minimumStaffing.map((line, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2 py-1">
            <input type="number" min={1} aria-label="Minimum" className={`${INPUT_CLASS} w-16 text-right font-bold`} value={line.minimum} onChange={(event) => setLine(index, { minimum: Math.max(1, Math.round(Number(event.target.value) || 1)) })} />
            <select aria-label="Role" className={INPUT_CLASS} value={line.role ?? ''} onChange={(event) => setLine(index, { role: event.target.value === '' ? null : (event.target.value as WorkerRole) })}>
              <option value="">of any role</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <span className="text-xs text-ink-mid">on</span>
            {rules.units.length > 0 ? (
              <select aria-label="Unit" className={INPUT_CLASS} value={line.unitId ?? ''} onChange={(event) => setLine(index, { unitId: event.target.value === '' ? null : event.target.value })}>
                <option value="">every unit</option>
                {rules.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name || unit.id}
                  </option>
                ))}
              </select>
            ) : (
              <CommitOnBlur label="Unit" placeholder="every unit" className={`${INPUT_CLASS} w-32 font-mono`} value={line.unitId ?? ''} onCommit={(text) => setLine(index, { unitId: text.trim() === '' ? null : text.trim() })} />
            )}
            <span className="text-xs text-ink-mid">each</span>
            <select aria-label="Shift type" className={INPUT_CLASS} value={line.shiftType} onChange={(event) => setLine(index, { shiftType: event.target.value as ShiftTypeKey })}>
              {(offeredTypes.length > 0 ? offeredTypes : (['day', 'evening', 'night'] as const)).map((key) => (
                <option key={key} value={key}>
                  {SHIFT_TYPE_LABELS[key]}
                </option>
              ))}
            </select>
            <span className="text-xs text-ink-mid">per</span>
            <input type="number" min={1} aria-label="Residents per minimum" placeholder="any census" className={`${INPUT_CLASS} w-24 text-right`} value={line.perResidents ?? ''} onChange={(event) => setLine(index, { perResidents: event.target.value === '' ? null : Math.max(1, Math.round(Number(event.target.value) || 1)) })} />
            <span className="text-xs text-ink-mid">residents</span>
            <button type="button" aria-label="Remove this minimum" className="p-1.5 text-ink-mid hover:text-coral" onClick={() => onChange({ minimumStaffing: rules.minimumStaffing.filter((_, at) => at !== index) })}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button icon={Plus} variant="secondary" size="sm" className="mt-2" onClick={() => onChange({ minimumStaffing: [...rules.minimumStaffing, { unitId: rules.units[0]?.id ?? null, shiftType: offeredTypes[0] ?? 'day', role: null, minimum: 1 }] })}>
          Add a minimum
        </Button>
      </div>

      <div>
        <div className="text-xs font-bold text-ink">Who may cover whom</div>
        <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">The seats a role may fill beside its own, where your state allows it - a nurse can usually cover a CNA seat, never the reverse, and an RN can cover an LPN. A covered seat counts toward its own role's minimum and target. Nothing ticked keeps every seat to its own role.</p>
        {ROLES.map((role) => (
          <div key={role} className="flex flex-wrap items-center gap-4 py-1.5 border-b border-marine/10 last:border-b-0">
            <span className="w-28 text-xs text-ink-mid">{role} may cover</span>
            {ROLES.filter((other) => other !== role).map((other) => {
              const covers = rules.substitutions?.[role] ?? []
              const checked = covers.includes(other)
              return (
                <label key={other} className="flex items-center gap-1.5 text-xs text-ink">
                  <input type="checkbox" aria-label={`${role} may cover a ${other} seat`} checked={checked} onChange={() => {
                    const next = checked ? covers.filter((entry) => entry !== other) : [...covers, other]
                    const { [role]: _dropped, ...others } = rules.substitutions ?? {}
                    onChange({ substitutions: next.length > 0 ? { ...others, [role]: next } : others })
                  }} />
                  {other} seats
                </label>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
